/* eslint-disable no-restricted-syntax */
/* eslint-disable no-await-in-loop */
/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk, createAction } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { requestStatusType } from '../../types';
import {
  finalizeUploads,
  getUploadUrls,
} from '../../services/fileSystemService';
import { ContentType } from '../../constants/mediaFormatProfiles';
import { addFile } from '../library/librarySlice';
import { setAlertMessage } from '../dashboard/dashboardSlice';
import {
  initializeUploadWithDebounce,
  finalizeUploadWithRetry,
  uploadSingleChunk,
} from '../../recorder/utils/uploadUtils';
import validateFiles from './fileValidation';
import { shouldRefreshToken } from '../../lib/localStorageHandler';
import { refreshToken } from '../auth/authThunks';
import getTranslation from '../../i18n/utils';

interface UploadProgress {
  folderId: string;
  fileId: string;
  fileName: string;
  progress: number; // Progress in percentage
}

interface UploadState {
  status: requestStatusType;
  progress: number; // Total progress
  activeUploads: UploadProgress[];
}

const initialState: UploadState = {
  status: 'idle',
  progress: 0,
  activeUploads: [],
};

// Custom action for progress
export const updateProgress = createAction<UploadProgress>('upload/progress');

// Define the thunk for uploading files
interface UploadFilesThunkArg {
  teamId: string;
  folderId: string;
  files: File[];
}

const MAX_DIRECT_UPLOAD_SIZE = 100 * 1024 * 1024; // 100 MiB
const UPLOAD_CHUNK_SIZE = 5 * 1024 * 1024; // 5 MiB

export const uploadFiles = createAsyncThunk(
  'upload/uploadFiles',
  async (
    { teamId, folderId, files }: UploadFilesThunkArg,
    { dispatch, rejectWithValue },
  ) => {
    if (shouldRefreshToken()) {
      await dispatch(refreshToken());
    }

    const validationResult = await validateFiles(files);

    if (!validationResult.isValid) {
      // Early return if validation fails
      return rejectWithValue({
        isValid: false,
        errorMessage: validationResult.errorMessage,
        errorType: validationResult.errorType,
      });
    }

    const filesNeedingConversion =
      validationResult.filesNeedingConversion || [];

    for (const file of files) {
      const fileSize = file.size;
      const fileName = file.name;
      const contentType = (file.type as ContentType) || 'video/x-matroska';

      if (fileSize > MAX_DIRECT_UPLOAD_SIZE) {
        // Multipart upload for large files
        try {
          const { uploadId, fileId } = await initializeUploadWithDebounce({
            teamId,
            folderId,
            contentType,
          });
          const totalChunks = Math.ceil(fileSize / UPLOAD_CHUNK_SIZE);

          for (let i = 0; i < totalChunks; i += 1) {
            const start = i * UPLOAD_CHUNK_SIZE;
            const end = Math.min(start + UPLOAD_CHUNK_SIZE, fileSize);
            const chunk = file.slice(start, end);
            const partNumber = i + 1;

            await uploadSingleChunk({
              teamId,
              folderId,
              fileId,
              uploadId,
              contentType,
              blob: chunk,
              partNumber,
            });

            const progress = Math.round(((i + 1) * 100) / totalChunks);
            dispatch(updateProgress({ folderId, fileId, fileName, progress }));
          }

          const teamFileEntity = await finalizeUploadWithRetry({
            teamId,
            folderId,
            fileId,
            uploadId,
            contentType,
            notes: [],
            title: fileName,
            needsConversion: filesNeedingConversion.includes(file),
          });
          dispatch(addFile(teamFileEntity));
        } catch (error) {
          dispatch(
            setAlertMessage({
              message: getTranslation('upload.errors.uploadFailed'),
              type: 'error',
            }),
          );
          return rejectWithValue(error);
        }
      } else {
        // Single presigned URL for smaller files
        try {
          const response = await getUploadUrls({
            teamId,
            folderId,
            files: [{ name: fileName, contentType }],
          });

          const { fileId, uploadUrl } = response.data.data.uploadParams[0];

          // Retry logic for non-multipart uploads (max 3 attempts)
          let uploadSuccess = false;
          let lastError = null;
          const maxRetries = 3;

          for (let attempt = 0; attempt < maxRetries; attempt += 1) {
            await new Promise((resolve) =>
              setTimeout(resolve, attempt * 1000 + 100),
            );
            try {
              await axios.put(uploadUrl, file, {
                headers: {
                  'Content-Type': contentType,
                },
                onUploadProgress: (progressEvent) => {
                  const { loaded, total } = progressEvent || {};
                  if (!loaded || !total) return;
                  const progress = Math.round((loaded * 100) / total);
                  dispatch(
                    updateProgress({ folderId, fileId, fileName, progress }),
                  );
                },
              });
              uploadSuccess = true;
              break; // Exit retry loop on success
            } catch (uploadError) {
              lastError = uploadError;
              if (attempt < maxRetries) {
                // Reset progress on retry
                dispatch(
                  updateProgress({ folderId, fileId, fileName, progress: 0 }),
                );
              }
            }
          }

          if (!uploadSuccess) {
            throw lastError;
          }

          const finalizeResponse = await finalizeUploads({
            teamId,
            folderId,
            fileId,
            contentType,
            name: fileName,
            needsConversion: filesNeedingConversion.includes(file),
          });

          dispatch(addFile(finalizeResponse.data.data.file));
        } catch (error) {
          dispatch(
            setAlertMessage({
              message: getTranslation('upload.errors.uploadFailed'),
              type: 'error',
            }),
          );
          return rejectWithValue(error);
        }
      }
    }
    // Always return a value at the end
    return { success: true };
  },
);

const uploadSlice = createSlice({
  name: 'upload',
  initialState,
  reducers: {
    resetUploadState: (state) => {
      state.status = 'idle';
      state.progress = 0;
      state.activeUploads = [];
    },
    // Used in recorder
    addFileToUploads: (
      state,
      action: PayloadAction<{
        folderId: string;
        fileId: string;
        fileName: string;
        progress: number;
      }>,
    ) => {
      state.activeUploads.push(action.payload);
    },
    // Used in recorder
    removeFileFromUploads: (
      state,
      action: PayloadAction<{
        folderId: string;
        fileId: string;
      }>,
    ) => {
      state.activeUploads = state.activeUploads.filter(
        (up) => up.fileId !== action.payload.fileId,
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(uploadFiles.pending, (state) => {
      state.status = 'pending';
    });
    builder.addCase(uploadFiles.fulfilled, (state) => {
      state.status = 'succeeded';
      state.progress = 100; // All uploads complete
      state.progress = 0;
      state.activeUploads = [];
    });
    builder.addCase(uploadFiles.rejected, (state) => {
      state.status = 'failed';
      state.progress = 0;
      state.activeUploads = [];
    });
    builder.addCase(updateProgress, (state, action) => {
      const { folderId, fileId, progress } = action.payload;
      const existingProgress = state.activeUploads.find(
        (up) => up.fileId === fileId,
      );

      if (existingProgress) {
        existingProgress.progress = progress;
      } else {
        state.activeUploads.push({
          folderId,
          fileId,
          fileName: action.payload.fileName,
          progress,
        });
      }

      // Calculate total progress
      const totalProgress = state.activeUploads.reduce(
        (total, file) => total + file.progress,
        0,
      );
      state.progress = totalProgress / state.activeUploads.length;
    });
  },
});

export const { resetUploadState, addFileToUploads, removeFileFromUploads } =
  uploadSlice.actions;

export default uploadSlice.reducer;
